import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlan } from '../../context/PlanContext';
import { useLifely } from '../../context/LifelyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';

const PlanGate = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useLifely();
  const { canAccessPath, hasAnyPlan, planName } = usePlan();

  const currentPath = location.pathname;

  // Navigate to upgrade page
  const handleUpgrade = () => {
    navigate('/join/upgrade');
  };

  // Navigate to pricing page for users with no plan
  const handleViewPlans = () => {
    navigate('/join/pricing');
  };

  // If not authenticated, AuthWrapper should handle this
  if (!isAuthenticated) {
    return children;
  }

  // If user has no plan at all
  if (!hasAnyPlan) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="no-plan-gate">
        <Card className="max-w-md w-full border-2 border-primary/20">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-serif">Membership Required</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              You need an active membership to access Lifely. Choose a plan that works for you.
            </p>
            <div className="space-y-3 pt-2">
              <Button 
                onClick={handleViewPlans} 
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="view-plans-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user has a plan but cannot access this specific page
  if (!canAccessPath(currentPath)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="upgrade-gate">
        <Card className="max-w-md w-full border-2 border-primary/20">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-serif">Upgrade Required</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your current <strong>{planName}</strong> plan doesn't include access to this feature.
            </p>
            <p className="text-sm text-muted-foreground">
              Upgrade to <strong>Standard</strong> or <strong>Plus</strong> to unlock all of Lifely's features, 
              including asset mapping, continuity planning, and more.
            </p>
            <div className="space-y-3 pt-2">
              <Button 
                onClick={handleUpgrade} 
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="upgrade-plan-btn"
              >
                Upgrade Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              With your Insights plan, you can access the Dashboard, Insights guides, and Settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User has access - render children
  return children;
};

export default PlanGate;
